/**
 * Table component for displaying form submissions
 */

import React from 'react';
import { Eye, Trash2, Mail, Clock } from 'lucide-react';
import { cn } from '../../utils/cn';
import type { FormSubmission } from '../../types/submissions';

interface SubmissionsTableProps {
  submissions: FormSubmission[];
  isLoading: boolean;
  onView: (submission: FormSubmission) => void;
  onDelete: (submission: FormSubmission) => void;
  isDeleting: boolean;
}

export const SubmissionsTable: React.FC<SubmissionsTableProps> = ({
  submissions,
  isLoading,
  onView,
  onDelete,
  isDeleting,
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  const getSubmissionPreview = (submission: FormSubmission): string => {
    const formData = (submission.formData || {}) as Record<string, unknown>;
    const entries = Object.entries(formData);
    const previewParts: string[] = [];

    for (const [key, value] of entries) {
      if (typeof value === 'string' && value.trim()) {
        // Skip common non-content fields
        if (['_honeypot', 'timestamp', 'formId'].includes(key)) continue;
        previewParts.push(String(value).substring(0, 50));
        if (previewParts.length >= 2) break;
      }
    }

    return previewParts.join(' • ') || 'No preview available';
  };

  if (isLoading && submissions.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
        <div className="w-8 h-8 mx-auto mb-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-gray-600">Loading submissions...</p>
      </div>
    );
  }

  if (submissions.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
        <Mail className="w-12 h-12 mx-auto mb-4 text-gray-400" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No submissions yet</h3>
        <p className="text-gray-600">
          Form submissions will appear here once visitors start using your forms.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Preview
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
              Email
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Date
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {submissions.map(submission => (
            <tr
              key={submission.id}
              className={cn(
                'hover:bg-gray-50 transition-colors cursor-pointer',
                !submission.isRead && 'bg-blue-50 hover:bg-blue-100'
              )}
              onClick={() => onView(submission)}
            >
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center space-x-2">
                  {!submission.isRead && (
                    <span className="w-2 h-2 bg-primary-600 rounded-full" title="Unread" />
                  )}
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900 truncate max-w-xs">
                  {getSubmissionPreview(submission)}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                {submission.submitterEmail ? (
                  <span className="text-sm text-gray-600">{submission.submitterEmail}</span>
                ) : (
                  <span className="text-sm text-gray-400 italic">No email</span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center text-sm text-gray-600">
                  <Clock className="w-4 h-4 mr-1" />
                  {formatDate(submission.submittedAt)}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right">
                <div
                  className="flex items-center justify-end space-x-2"
                  onClick={e => e.stopPropagation()}
                >
                  <button
                    onClick={() => onView(submission)}
                    className="p-1 text-gray-600 hover:text-primary-600 rounded transition-colors"
                    title="View"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDelete(submission)}
                    disabled={isDeleting}
                    className="p-1 text-gray-600 hover:text-red-600 rounded transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
